/**
 * Немой слепок входа в вилку.
 *
 * Это не attempt. Attempt пишет исход ставки. Слепок пишет миг, когда человек
 * открыл конторы: что стояло на ленте, какой банк и сито, какие URL ушли в
 * `window.open`. Сервер сам допишет, что держали адаптеры.
 *
 * Вызывать после обоих `window.open`: `fetch` даже без `await` сжигает жест,
 * и второе окно Edge кладёт вкладкой в сканер (`act.ts`). `keepalive` нужен
 * тем же, чем у attempt: обычный fetch браузер отменит, когда фокус уйдёт
 * на контору.
 *
 * Клиент `held` не шлёт. Если прислал — сервер игнорирует.
 */

import { apiUrl } from './build';
import type { Fork } from './protocol';
import { flashCode } from '../next/sightFlash';
import { useFilters } from '../next/filters';
import { useProfile } from '../next/profile';
import { useScreen } from '../next/store';
import { useSettings } from '../store/settings';
import type { PageClip } from '../confirm/clip';
import { lieOf } from '../confirm/lie';

export const DEDUP_MS = 2000;

export interface SightPriced {
  profit: string;
  staked: string;
  payout: string;
  roiPct: string;
}

export interface SightBody {
  id: string;
  at: string;
  urls: string[];
  fork: Fork;
  priced?: SightPriced;
  money: {
    bank: string;
    fx: string;
    gate: string;
    caps: Record<string, string>;
  };
  sieve: {
    venues: string[];
    venueMode: string;
    sports: string[];
    markets: string[];
    lines: string[];
    roiMin: string | null;
    roiMax: string | null;
    ageMax: string | null;
    sort: string;
  };
  profile: string | null;
  openedLegs: number[];
}

const lastAt = new Map<string, number>();
let lastPageKey = '';

/** Тесты не оставляют чужой дедуп следующей проверке. */
export function resetRemember(): void {
  lastAt.clear();
  lastPageKey = '';
}

/**
 * Улика проверки — в тот же слепок, не во второй шкаф.
 * Вердикт оператора отсюда не ставим: только `seen.pages` и `seen.pageLie`.
 */
export function notePages(clips: readonly PageClip[], extra?: { forkId?: string }): void {
  if (clips.length === 0) return;
  const key = clips.map((one) => `${one.venue}:${one.ours}:${one.closest}:${one.state}`).join('|');
  if (key === lastPageKey) return;
  lastPageKey = key;
  const lie = lieOf(clips);
  void fetch(apiUrl('/v1/sighting/page', extra?.forkId), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      pages: clips,
      pageLie: lie,
      forkId: extra?.forkId ?? '',
    }),
    keepalive: true,
  }).catch(() => {
    // Молча: журнал — наш инструмент.
  });
}

export function note(body: SightBody): void {
  void fetch(apiUrl('/v1/sighting', body.id), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  })
    .then(async (res) => {
      if (!res.ok) return;
      const data: unknown = await res.json();
      if (
        data !== null &&
        typeof data === 'object' &&
        'code' in data &&
        typeof (data as { code: unknown }).code === 'string'
      ) {
        // Тост не копирует в буфер: там сумма ставки.
        flashCode((data as { code: string }).code);
      }
    })
    .catch(() => {
      // Молча: журнал — наш инструмент, а не работа человека.
    });
}

export function remember(
  id: string,
  urls: readonly string[],
  extra?: { priced?: SightPriced; openedLegs?: number[] },
): void {
  if (!id || urls.length === 0) return;
  const now = Date.now();
  const prev = lastAt.get(id);
  const steps = useScreen.getState().steps[id];
  const opened = steps?.opened ?? [];
  const fresh = opened.length === 0 || opened.every((at) => at === null);
  if (prev !== undefined && now - prev < DEDUP_MS) return;
  if (prev !== undefined && !fresh && now - prev < DEDUP_MS) return;
  lastAt.set(id, now);
  const body = packSeen(id, urls, extra);
  if (body === null) return;
  note(body);
}

export function packSeen(
  id: string,
  urls: readonly string[],
  extra?: { priced?: SightPriced; openedLegs?: number[] },
): SightBody | null {
  const screen = useScreen.getState();
  const fork = screen.forks.get(id) ?? screen.ghosts.get(id)?.fork;
  if (fork === undefined) return null;
  const settings = useSettings.getState();
  const filters = useFilters.getState();
  const profile = useProfile.getState().profile;
  const caps: Record<string, string> = {};
  for (const [source, value] of Object.entries(settings.caps)) {
    caps[source] = String(value);
  }
  return {
    id,
    at: new Date().toISOString(),
    urls: [...urls],
    fork,
    priced: extra?.priced,
    money: {
      bank: String(settings.bank),
      fx: String(settings.fx),
      gate: String(settings.gate),
      caps,
    },
    sieve: {
      venues: [...filters.venues],
      venueMode: filters.venueMode,
      sports: [...filters.sports],
      markets: [...filters.markets],
      lines: [...filters.lines],
      roiMin: filters.roiMin === null ? null : String(filters.roiMin),
      roiMax: filters.roiMax === null ? null : String(filters.roiMax),
      ageMax: filters.ageMax === null ? null : String(filters.ageMax),
      sort: filters.sort,
    },
    profile: profile?.name ?? null,
    openedLegs: extra?.openedLegs ?? [],
  };
}
